import { useEffect, useState } from "react";
import { Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { FilamentPicker } from "@/components/ds/FilamentPicker";

const norm = (v) =>
  (v || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const maskHm = (value) => {
  const digits = String(value).replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, digits.length - 2)}:${digits.slice(-2)}`;
};

const hmToMinutes = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length <= 2) return Number(digits) * 60;
  return Number(digits.slice(0, -2)) * 60 + Number(digits.slice(-2));
};

export function DevPlateModal({ projectId, projectName, linked = [], onClose, onSaved }) {
  const [tab, setTab] = useState("new");
  const [plates, setPlates] = useState([]);
  const [filaments, setFilaments] = useState([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState(projectName || "");
  const [outputQuantity, setOutputQuantity] = useState("1");
  const [printTime, setPrintTime] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/plates"), api.get("/filaments")])
      .then(([p, f]) => {
        setPlates(p.data);
        setFilaments(f.data);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const link = async (plateId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${projectId}/plates`, { id: plateId });
      onSaved(data);
      toast.success("Plate vinculada ao projeto.");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createAndLink = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Adicione pelo menos um filamento à Plate.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${projectId}/plates/new`, {
        name,
        plate_type: "COMPLETO",
        output_quantity: Number(outputQuantity || 1),
        print_time_min: hmToMinutes(printTime),
        notes,
        filaments: items.map((i) => ({
          filament_id: i.filament_id,
          quantity_g: Number(i.quantity_g),
        })),
      });
      onSaved(data);
      toast.success("Plate criada e vinculada ao projeto.");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const visible = plates.filter((p) => {
    if (linked.includes(p.id)) return false;
    if (!search.trim()) return true;
    return norm(p.name).includes(norm(search));
  });

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Plate do projeto"
      description="A Plate criada aqui é uma Plate real do sistema, já vinculada ao projeto."
      testid="dev-plate-modal"
    >
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => setTab("new")}
          data-testid="plate-tab-new"
          className={`tr-chip ${tab === "new" ? "tr-chip-active" : ""}`}
        >
          Criar Plate
        </button>
        <button
          onClick={() => setTab("existing")}
          data-testid="plate-tab-existing"
          className={`tr-chip ${tab === "existing" ? "tr-chip-active" : ""}`}
        >
          Vincular existente
        </button>
      </div>

      {tab === "existing" ? (
        <div className="mt-4 space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar Plate..."
              className="pl-9"
              data-testid="dev-plate-search"
            />
          </div>
          <div className="max-h-[45vh] space-y-2 overflow-y-auto">
            {visible.length === 0 && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                Nenhuma Plate disponível para vincular.
              </p>
            )}
            {visible.map((p) => (
              <button
                key={p.id}
                disabled={busy}
                onClick={() => link(p.id)}
                data-testid={`pick-plate-${p.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5 text-left transition-colors hover:border-brand/40"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">{p.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.product_name || "Sem produto"} · {p.output_quantity} un
                  </p>
                </div>
                <span className="shrink-0 font-num text-xs text-muted-foreground">
                  {gramas(p.total_g)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form onSubmit={createAndLink} className="mt-4 flex flex-col gap-4">
          <Field label="Nome da Plate">
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="dev-plate-name" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Peças por impressão">
              <Input
                type="number"
                min="1"
                value={outputQuantity}
                onChange={(e) => setOutputQuantity(e.target.value)}
                className="font-num"
                data-testid="dev-plate-output"
              />
            </Field>
            <Field label="Tempo de impressão (h:min)">
              <Input
                value={printTime}
                onChange={(e) => setPrintTime(maskHm(e.target.value))}
                placeholder="2:30"
                inputMode="numeric"
                className="font-num"
                data-testid="dev-plate-time"
              />
            </Field>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Filamentos da Plate</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="dev-plate-add-filament-btn"
                className="h-8 gap-1.5 rounded-full text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Adicionar
              </Button>
            </div>
            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/15 bg-secondary/30 p-3 text-xs text-muted-foreground">
                Nenhum filamento escolhido.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((i, idx) => {
                  const f = filaments.find((x) => x.id === i.filament_id);
                  return (
                    <div
                      key={i.filament_id}
                      className="flex items-center gap-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2"
                      data-testid={`dev-plate-item-${i.filament_id}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {f ? `${f.material} ${f.color_name}` : "Filamento"}
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={i.quantity_g}
                        onChange={(e) =>
                          setItems((list) =>
                            list.map((x, k) => (k === idx ? { ...x, quantity_g: e.target.value } : x))
                          )
                        }
                        placeholder="g"
                        className="h-9 w-24 font-num"
                        data-testid={`dev-plate-item-qty-${i.filament_id}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setItems((list) => list.filter((_, k) => k !== idx))}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {pickerOpen && (
              <div className="mt-2 rounded-lg border border-white/10 bg-background/60 p-3">
                <FilamentPicker
                  filaments={filaments.filter((f) => !items.some((i) => i.filament_id === f.id))}
                  onSelect={(f) => {
                    setItems((list) => [...list, { filament_id: f.id, quantity_g: "" }]);
                    setPickerOpen(false);
                  }}
                  testid="dev-plate-filament-picker"
                />
              </div>
            )}
          </div>

          <Field label="Observações">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="dev-plate-notes" />
          </Field>
          <Button
            type="submit"
            disabled={busy}
            data-testid="save-dev-plate-btn"
            className="h-12 rounded-full font-semibold transition-transform active:scale-95"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar e vincular Plate
          </Button>
        </form>
      )}
    </ResponsiveModal>
  );
}
